import {
  exact,
  fallback,
  readString,
  segment,
  type V2ConsoleTarget
} from '@/utils/v2-console-target/shared'

const kafkaClusterTabs: Record<string, string> = {
  KafkaClusterPage: 'overview',
  KafkaClusterDashboard: 'overview',
  KafkaClusterOverview: 'details',
  LakehouseTableForNativeKafkaOverview: 'lakehouse-table'
}

const kafkaTopicTabs: Record<string, string> = {
  KafkaTopicPage: 'dashboard',
  KafkaTopicMetrics: 'dashboard',
  KafkaTopicSchema: 'schema',
  KafkaTopicMessages: 'messages',
  KafkaTopicConfiguration: 'configuration'
}

const kafkaOnlyRoutes = new Set([
  ...Object.keys(kafkaClusterTabs),
  ...Object.keys(kafkaTopicTabs),
  'KafkaClusterConfiguration',
  'KafkaTopicsPage',
  'KafkaTopicClients',
  'KafkaTopicStats',
  'KafkaSubjectSchemaRegistry'
])

type KafkaTargetContext = {
  routeName: string
  params?: Record<string, unknown>
  kafkaClusterBase?: string
  instanceFallback: V2ConsoleTarget
  topic?: string
  consumerGroupId?: string
}

export function resolveKafkaTarget({
  routeName,
  params,
  kafkaClusterBase,
  instanceFallback,
  topic,
  consumerGroupId
}: KafkaTargetContext): V2ConsoleTarget | undefined {
  if (!kafkaClusterBase) {
    return kafkaOnlyRoutes.has(routeName) ? instanceFallback : undefined
  }

  const clusterFallback = fallback(`${kafkaClusterBase}/overview`, 'kafkaCluster')
  const clusterTab = kafkaClusterTabs[routeName]
  if (clusterTab !== undefined) return exact(`${kafkaClusterBase}/${clusterTab}`)

  if (routeName === 'KafkaClusterConfiguration') {
    return fallback(`${kafkaClusterBase}/details`, 'kafkaClusterDetails')
  }
  if (routeName === 'KafkaClientsPage') return exact(`${kafkaClusterBase}/clients`)
  if (routeName === 'SchemaListPage') return exact(`${kafkaClusterBase}/schema-registry`)
  if (routeName === 'ConsumerGroupListPage') return exact(`${kafkaClusterBase}/consumer-groups`)
  if (routeName === 'ConsumerGroupPage') {
    return consumerGroupId
      ? exact(`${kafkaClusterBase}/consumer-groups/${segment(consumerGroupId)}`)
      : clusterFallback
  }
  if (routeName === 'KafkaTopicsPage') return exact(`${kafkaClusterBase}/topics`)

  const topicBase = topic ? `${kafkaClusterBase}/topics/${segment(topic)}` : undefined
  const topicFallback = topicBase ? fallback(topicBase, 'kafkaTopic') : clusterFallback
  const topicTab = kafkaTopicTabs[routeName]
  if (topicTab !== undefined) {
    return topicBase ? exact(`${topicBase}/${topicTab}`) : clusterFallback
  }
  if (routeName === 'KafkaTopicClients') return topicFallback
  if (routeName === 'KafkaTopicStats') {
    return topicBase ? fallback(`${topicBase}/dashboard`, 'kafkaTopic') : clusterFallback
  }

  if (routeName === 'KafkaSubjectSchemaRegistry') {
    const subject = readString(params, 'subject')
    return subject
      ? exact(`${kafkaClusterBase}/schema-registry/${segment(subject)}`)
      : clusterFallback
  }

  return undefined
}
